import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calendar, Eye, ArrowLeft, Camera, User, Loader2 } from 'lucide-react';
import BlockRenderer from '../../components/ui/BlockRenderer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryLabels = {
  class_photo: 'Class Photos',
  news: 'News & Updates',
  success_story: 'Success Stories',
  event: 'Events',
  announcement: 'Announcements',
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/blog/${slug}/`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="section-padding">
        <div className="container-app flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="section-padding">
        <div className="container-app text-center py-24">
          <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding">
      <div className="container-app max-w-4xl">
        {/* Back Button */}
        <Link to="/blog" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Link>

        {/* Post Header */}
        <article>
          <header className="mb-8">
            <Badge variant="secondary" className="mb-4">
              {categoryLabels[post.category] || post.category}
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {post.author_name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {post.view_count} views
              </span>
            </div>
          </header>

          {/* Featured Image */}
          {post.featured_image_url && (
            <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-8">
              <img 
                src={post.featured_image_url} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          {post.content_blocks && post.content_blocks.length > 0 ? (
            <div className="mb-8">
              <BlockRenderer blocks={post.content_blocks} />
            </div>
          ) : post.content ? (
            <div className="prose prose-lg max-w-none mb-8">
              {post.content.split('\n').map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>
          ) : null}

          {/* Photo Gallery */}
          {post.photo_gallery && post.photo_gallery.length > 0 && (
            <div className="mb-8">
              <h3 className="font-semibold text-xl mb-4 flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Photo Gallery
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {post.photo_gallery.map((photo, idx) => (
                  <div 
                    key={idx} 
                    className="aspect-square bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedImage(photo)}
                  >
                    <img 
                      src={photo.url} 
                      alt={photo.caption || `Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/400x400?text=Photo';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag, idx) => (
                <Badge key={idx} variant="outline">
                  #{tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Related Course */}
          {post.course_title && (
            <Card className="bg-muted/50">
              <CardContent className="p-6">
                <h4 className="font-semibold mb-2">Related Course</h4>
                <p className="text-muted-foreground">{post.course_title}</p>
                <Link to="/catalog">
                  <Button variant="link" className="px-0 mt-2">
                    View Course Catalog →
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </article>

        {/* Image Lightbox */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="max-w-4xl max-h-[90vh] relative">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.caption || 'Gallery image'}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
              {selectedImage.caption && (
                <p className="text-white text-center mt-4">{selectedImage.caption}</p>
              )}
              <button 
                className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70"
                onClick={() => setSelectedImage(null)}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
